import { compactNumber } from "@/lib/formatNumber";
import { avatarSrc } from "@/lib/defaults";
import React, { useCallback, useEffect, useState } from "react";
import { useUserBlocked } from "@/lib/blocks";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { api } from "@/lib/api";
import { sharedGet, invalidateShared } from "@/lib/sharedGet";
import { whenFeedReady } from "@/lib/lowPriority";
import { mutualLabel } from "@/lib/mutual";
import { MutualLine } from "@/components/MutualLine";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserPlus, Check, X } from "lucide-react";

const roleLabel = (r, t) => ({ preacher: t("role.preacher", "Prédicateur"), artist: t("role.artist", "Artiste"), influencer: t("role.influencer", "Influenceur(e)") }[r] || "");

// People-you-may-follow. variant="feed" (horizontal cards) | "sidebar" (list).
// Lot 530 : « Suivi(e) par … » sous le nom, croix = masquer pour de bon (POST /users/suggestions/{id}/hide), « Voir tout » → onglet Suggestions.
export const FollowSuggestions = ({ variant = "sidebar" }) => {
  const { user, t } = useApp();
  const [items, setItems] = useState([]);
  useUserBlocked(useCallback((uid) => setItems((arr) => arr.filter((u) => u.id !== uid)), []));
  const [followed, setFollowed] = useState({});

  useEffect(() => {
    if (!user) return;
    // Après le 1er rendu du fil ; une seule requête pour les deux emplacements (barre latérale + fil).
    return whenFeedReady(() => { sharedGet("/users/suggestions", { ttl: 60000 }).then((data) => setItems(data.users || [])).catch(() => {}); });
  }, [user]);
  useEffect(() => {
    // Masquée depuis l'autre emplacement (fil ↔ barre latérale) → retirée ici aussi.
    const onHidden = (e) => setItems((arr) => arr.filter((x) => x.id !== e.detail));
    window.addEventListener("yalola:suggestion-hidden", onHidden);
    return () => window.removeEventListener("yalola:suggestion-hidden", onHidden);
  }, []);

  const follow = async (u) => {
    setFollowed((f) => ({ ...f, [u.id]: true }));
    try { await api.post(`/users/${u.id}/follow`); } catch { setFollowed((f) => ({ ...f, [u.id]: false })); }
  };
  const dismiss = async (u) => {
    window.dispatchEvent(new CustomEvent("yalola:suggestion-hidden", { detail: u.id }));
    setItems((arr) => arr.filter((x) => x.id !== u.id));
    try {
      await api.post(`/users/suggestions/${u.id}/hide`);
      invalidateShared("/users/suggestions");
      toast.success(t("suggestions.hidden_toast", "Suggestion masquée — on ne te la proposera plus."));
    } catch { /* la carte est déjà retirée à l'écran */ }
  };

  if (!user || items.length === 0) return null;

  const Badge = ({ u }) => (u.role === "preacher" || u.role === "artist" || u.role === "influencer") ? (
    <span className="inline-flex items-center gap-0.5 text-[10px]" style={{ color: "var(--yalola-accent)" }}>
      {u.is_verified && <VerifiedBadge user={u} size={11} />} {roleLabel(u.role, t)}
    </span>
  ) : null;
  const Mutual = ({ u, className = "" }) => (mutualLabel(u.mutual, t)
    ? <div className={`text-[11px] flex justify-center min-w-0 ${className}`} style={{ color: "var(--yalola-text-muted)" }}><MutualLine mutual={u.mutual} t={t} size={16} testId={`suggestion-mutual-${u.id}`} /></div>
    : null);
  const HideBtn = ({ u, className = "" }) => (
    <button type="button" onClick={() => dismiss(u)} className={`opacity-50 hover:opacity-100 ${className}`} aria-label={t("suggestions.hide", "Masquer cette suggestion")} title={t("suggestions.hide", "Masquer cette suggestion")} data-testid={`suggestion-dismiss-${u.id}`}><X size={14} /></button>
  );

  if (variant === "feed") {
    return (
      <div className="yalola-card p-4" data-testid="follow-suggestions-feed">
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold font-display">{t("suggestions.title", "Suggestions pour toi")}</div>
          <Link to={`/profile/${user.id}/suggestions`} className="text-xs" style={{ color: "var(--yalola-primary)" }} data-testid="suggestions-see-all">{t("common.see_all", "Voir tout")}</Link>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
          {items.map((u) => (
            <div key={u.id} className="shrink-0 w-40 rounded-2xl border p-3 text-center relative" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }} data-testid={`suggestion-${u.id}`}>
              <HideBtn u={u} className="absolute top-1.5 right-1.5" />
              <Link to={`/profile/${u.id}`}>
                <Avatar className="h-16 w-16 mx-auto"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
              </Link>
              <Link to={`/profile/${u.id}`} className="block mt-2 font-semibold text-sm truncate hover:underline">{u.name}</Link>
              <div className="h-4"><Badge u={u} /></div>
              {u.mutual?.count
                ? <Mutual u={u} className="mb-2" />
                : <div className="text-[11px] mb-2" style={{ color: "var(--yalola-text-muted)" }}>{compactNumber(u.follower_count || 0)} {t("suggestions.followers", "abonné(s)")}</div>}
              <button
                onClick={() => follow(u)}
                disabled={followed[u.id]}
                className="pill w-full justify-center text-xs"
                style={followed[u.id]
                  ? { background: "var(--yalola-surface)", color: "var(--yalola-text-muted)", border: "1px solid var(--yalola-border)" }
                  : { background: "var(--yalola-primary)", color: "#fff" }}
                data-testid={`suggestion-follow-${u.id}`}
              >
                {followed[u.id] ? <><Check size={13} className="mr-1" /> {t("suggestions.followed", "Suivi(e)")}</> : <><UserPlus size={13} className="mr-1" /> {t("suggestions.follow", "Suivre")}</>}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-1" data-testid="follow-suggestions-sidebar">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="text-[13px] font-bold" style={{ color: "var(--yalola-text-muted)" }}>{t("suggestions.title", "Suggestions pour toi")}</div>
        <Link to={`/profile/${user.id}/suggestions`} className="text-[11px]" style={{ color: "var(--yalola-primary)" }} data-testid="suggestions-see-all-sidebar">{t("common.see_all", "Voir tout")}</Link>
      </div>
      <div className="space-y-3 px-1">
        {items.slice(0, 5).map((u) => (
          <div key={u.id} className="flex items-center gap-2 group" data-testid={`suggestion-${u.id}`}>
            <Link to={`/profile/${u.id}`}>
              <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/profile/${u.id}`} className="block text-sm font-semibold truncate hover:underline">{u.name}</Link>
              {u.mutual?.count ? <Mutual u={u} /> : <Badge u={u} />}
            </div>
            <button
              onClick={() => follow(u)}
              disabled={followed[u.id]}
              className="pill text-xs shrink-0"
              style={followed[u.id]
                ? { background: "var(--yalola-surface)", color: "var(--yalola-text-muted)", border: "1px solid var(--yalola-border)" }
                : { background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}
              data-testid={`suggestion-follow-${u.id}`}
            >
              {followed[u.id] ? <Check size={14} /> : <UserPlus size={14} />}
            </button>
            <HideBtn u={u} className="shrink-0 h-7 w-7 grid place-items-center rounded-full hover:bg-black/5" />
          </div>
        ))}
      </div>
    </div>
  );
};
